import { useApp } from '@/hooks/useApp'
import type React from 'react'
import styled from 'styled-components'
import { device } from '@/styles/device'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`

const PageButton = styled.button<{
  isActive: boolean
  color: string
  subTextColor: string
}>`
  margin: 0 5px;
  padding: 5px 10px;
  background-color: transparent;
  color: ${(props) => (props.isActive ? props.color : props.subTextColor)};
  border: ${(props) =>
    props.isActive ? `1px solid ${props.color}` : `0px solid ${props.color}`};
  border-radius: 50%;
  cursor: pointer;
  width: 35px; // ボタンの幅を指定
  height: 35px; // ボタンの高さを指定
  display: flex;
  align-items: center;
  justify-content: center;
`

const ArrowButton = styled.button<{
  disabled: boolean
  disableColor: string
  subTextColor: string
}>`
  margin: 0 5px;
  padding: 5px;
  background-color: transparent;
  color: ${(props) =>
    props.disabled ? props.disableColor : props.subTextColor};
  border: none;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`
const Ellipsis = styled.span`
  margin: 0 5px;
  padding: 5px 10px;
  align-items: center;
  justify-content: center;
`

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange
}) => {
  const app = useApp()
  const handlePageChange = (page: number): void => {
    onPageChange(page)
    window.scroll({ top: 0 })
  }

  const primaryColor = app.theme.primaryMainColor.color ?? 'black'
  const arrowButtonDisableColor = 'rgba(0, 0, 0, 0.1)'
  const subTextColor = app.theme.subTextColor.color ?? 'black'

  const renderPageButtonsForMobile = (): React.ReactElement[] => {
    const buttons = []

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(
          <PageButton
            key={i}
            isActive={currentPage === i}
            onClick={() => {
              handlePageChange(i)
            }}
            color={primaryColor}
            subTextColor={subTextColor}
          >
            {i}
          </PageButton>
        )
      }
    } else {
      let startPage = currentPage - 2
      let endPage = currentPage + 2

      if (startPage <= 0) {
        startPage = 1
        endPage = 5
      } else if (endPage > totalPages) {
        startPage = totalPages - 4
        endPage = totalPages
      }

      for (let i = startPage; i <= endPage; i++) {
        buttons.push(
          <PageButton
            key={i}
            isActive={currentPage === i}
            onClick={() => {
              handlePageChange(i)
            }}
            color={primaryColor}
            subTextColor={subTextColor}
          >
            {i}
          </PageButton>
        )
      }

      if (startPage > 1) {
        buttons.shift()
        buttons.unshift(<Ellipsis key="start-ellipsis">...</Ellipsis>)
      }
      if (endPage < totalPages) {
        buttons.pop()
        buttons.push(<Ellipsis key="end-ellipsis">...</Ellipsis>)
      }
    }

    return buttons
  }

  const renderPageButtonsForLaptop = (): React.ReactElement[] => {
    const buttons = []

    let startPage = Math.max(1, currentPage - 2)
    const endPage = Math.min(startPage + 4, totalPages)

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4)
    }

    if (totalPages > 7 && startPage !== 1) {
      buttons.push(
        <PageButton
          key={1}
          isActive={currentPage === 1}
          onClick={() => {
            handlePageChange(1)
          }}
          color={primaryColor}
          subTextColor={subTextColor}
        >
          1
        </PageButton>
      )

      if (startPage > 2) {
        buttons.push(<Ellipsis key="startEllipsis">...</Ellipsis>)
        startPage++
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <PageButton
          key={i}
          isActive={currentPage === i}
          onClick={() => {
            handlePageChange(i)
          }}
          color={primaryColor}
          subTextColor={subTextColor}
        >
          {i}
        </PageButton>
      )
    }

    if (totalPages > 7 && endPage !== totalPages) {
      if (totalPages - endPage > 1) {
        buttons.pop()
        buttons.push(<Ellipsis key="endEllipsis">...</Ellipsis>)
      }

      buttons.push(
        <PageButton
          key={totalPages}
          isActive={currentPage === totalPages}
          onClick={() => {
            handlePageChange(totalPages)
          }}
          color={primaryColor}
          subTextColor={subTextColor}
        >
          {totalPages}
        </PageButton>
      )
    }

    return buttons
  }

  const handlePreviousPage = (): void => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1)
    }
  }

  const handleNextPage = (): void => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1)
    }
  }

  const isMobile = window.matchMedia(device.mobile).matches

  return (
    <PaginationContainer>
      <ArrowButton
        disabled={currentPage === 1}
        onClick={handlePreviousPage}
        disableColor={arrowButtonDisableColor}
        subTextColor={subTextColor}
      >
        {isMobile ? '<' : '<  前のページ'}
      </ArrowButton>
      {isMobile ? renderPageButtonsForMobile() : renderPageButtonsForLaptop()}
      <ArrowButton
        disabled={currentPage === totalPages}
        onClick={handleNextPage}
        disableColor={arrowButtonDisableColor}
        subTextColor={subTextColor}
      >
        {isMobile ? '>' : '次のページ  >'}
      </ArrowButton>
    </PaginationContainer>
  )
}

export default Pagination
